import React from "react";
import { connect } from "react-redux";
import { Button, message, Modal, Tooltip, Upload } from "antd";
import { Icon } from "@ant-design/compatible";
import { ExcelRenderer } from "react-excel-renderer";
import { importCustomers } from "../../../../../../redux/actions/customerActions";
import strings from "../../../../../../strings";

const { Dragger } = Upload;

const CSV_TEMPLATE_URL =
  "https://loystar.co/wp-content/uploads/2021/02/Customer_Import_Template.csv";


const GOOGLE_CSV_TEMPLATE_URL =
  "https://docs.google.com/spreadsheets/d/13onNBZhGQuNBmSJH3zv4XTBiHEgGdwWqlCKW8Wzn8Bs/edit#gid=1546638574";

class ImportCustomerModal extends React.Component {
  state = {
    fileList: [],
    isUploading: false,
    errors: [],
    strangeHeaders: [],
    rows: [],
  };

  handleUpload = () => {
    const { fileList } = this.state;
    const { user, importCustomers } = this.props;

    this.setState({ isUploading: true });

    const formData = new FormData();

    const data = { merchant_email: user.email, csv_file: fileList[0] };

    for (const key in data) {
      formData.append(key, data[key]);
    }

    importCustomers(formData)
      .then(res => {
        this.setState({ isUploading: false });

        if (res && res.status === 200) {
          this.setState({ fileList: [] });
          Modal.success({
            title: strings.yourFileQueuedForUpload,
          });
        }
      })
      .catch(err => {
        console.log(err);
        this.setState({ isUploading: false });
        Modal.error({
          title: strings.thereWasAnErrorPerformingOperation,
        });
      });
  };

  render() {
    const {
      isCustomerImportModalVisible,
      toggleCustomerImportModal,
    } = this.props;

    const { isUploading, strangeHeaders, errors } = this.state;

    const props = {
      name: "csv_file",
      multiple: false,
      onRemove: file => {
        this.setState(state => {
          const index = state.fileList.indexOf(file);
          const newFileList = state.fileList.slice();
          newFileList.splice(index, 1);
          return {
            fileList: newFileList,
          };
        });

        this.setState({ errors: [] });
      },
      beforeUpload: async file => {
        // Clear Any Errors or Files if Present
        this.setState({ fileList: [], errors: [], strangeHeaders: [] });

        if (!file) {
          message.error(strings.pleaseSelectFile, 5);
          return;
        }

        // Check If File is CSV File
        const isCsv = new RegExp("(.*?).(csv)$");

        if (!isCsv.test(file.name)) {
          this.setState({
            errorMessage: strings.oopsOnlyCsvFilesAllowed,
          });
          message.error(strings.oopsOnlyCsvFilesAllowed, 5);
          return;
        }

        const isLt5M = file.size / 1024 / 1024 < 5;

        if (!isLt5M) {
          this.setState({
            errorMessage: strings.fileShouldBeSmallerThan5Mb,
          });
          message.error(strings.fileShouldBeSmallerThan5Mb, 5);
          return;
        }

        let errors = [];
        let strangeHeaders = []; // Headers Not Accepted in CSV

        // Define accepted headers for customer import
        const acceptedHeaders = [
          "first name",
          "first_name", 
          "last name",
          "last_name",
          "email",
          "phone number",
          "phone_number",
          "date of birth", 
          "date_of_birth",
          "gender",
          "sex",
          "address",
          "address line 1",
          "address_line_1",
          "address line 2", 
          "address_line_2",
          "postcode",
          "post code",
          "postal code",
          "zip code",
          "city",
          "state",
          "country",
          "customer group",
          "customer_group"
        ]; // Headers Accepted in CSV File

        try {
          const resp = await ExcelRenderer(file);
          
          const {
            rows: [incomingHeaders, ...rest],
          } = resp;

          // Check for invalid headers
          incomingHeaders.forEach(val => {
            if (val && !acceptedHeaders.includes(val.toLowerCase().trim())) {
              strangeHeaders.push(val);
            }
          });

          if (strangeHeaders.length === 0) {
            // All headers are valid, accept the file
            const recordCount = rest.length;
            this.setState(state => ({
              fileList: [...state.fileList, file],
              rows: rest,
            }), () => {
              message.success(`You have ${recordCount} records in this file. Click upload to proceed with uploading.`, 5);
            });
          } else {
            // Some headers are invalid
            this.setState({
              errors: [`Invalid column headers found: ${strangeHeaders.join(", ")}`],
              strangeHeaders
            });
            message.error(`Invalid column headers: ${strangeHeaders.join(", ")}`, 5);
          }
        } catch (error) {
          console.error("Error parsing CSV file:", error);
          this.setState({
            errorMessage: "Error reading file. Please check the file format.",
          });
          message.error("Error reading file. Please check the file format.", 5);
        }

        return false;
      },
      fileList: this.state.fileList,
    };

    return (
      <Modal
        title={strings.importCustomers}
        visible={isCustomerImportModalVisible}
        destroyOnClose
        onCancel={toggleCustomerImportModal}
        footer={[
          <Button
            key="upload-btn"
            onClick={this.handleUpload}
            type="primary"
            disabled={this.state.fileList.length === 0}
            loading={isUploading}
          >
            {strings.upload}
          </Button>,
        ]}
      >
        <div className="product-import-wrapper">
          <p className="tutorial-wrapper">
            <span></span>

            <Tooltip
              title={
                <span>
                  Click this icon to download the CSV file template.{" "}
                  <a href={GOOGLE_CSV_TEMPLATE_URL} rel="noopener" target="blank">
                    (Get the Google sheet version)
                  </a>
                </span>
              }
            >
              <a
                href={CSV_TEMPLATE_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Icon type="download" style={{ fontSize: "30px" }} />
              </a>
            </Tooltip>
          </p>

          <Dragger {...props} customRequest={() => {}}>
            <p className="ant-upload-drag-icon">
              <Icon type="inbox" />
            </p>
            <p className="ant-upload-text">{strings.clickOrDragCustomerFileToUpload}</p>
          </Dragger>

          {/* Show selected file */}
          {(() => {
            console.log("Render check - fileList.length:", this.state.fileList.length);
            return this.state.fileList.length > 0;
          })() && (
            <div style={{ 
              marginTop: "1rem", 
              padding: "0.75rem", 
              backgroundColor: "#f6ffed", 
              border: "1px solid #b7eb8f", 
              borderRadius: "4px" 
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ color: "#52c41a", fontWeight: "500" }}>
                  <Icon type="file-text" style={{ marginRight: "8px" }} />
                  {this.state.fileList[0].name}
                </span>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    const fileName = this.state.fileList[0]?.name;
                    this.setState({ fileList: [], errors: [], strangeHeaders: [] });
                    message.info(`File "${fileName}" removed`, 2);
                  }}
                  style={{ color: "#52c41a" }}
                >
                  <Icon type="close" /> Remove
                </Button>
              </div>
              <div style={{ fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
                You have {this.state.rows.length} records in this file. Click upload to proceed with uploading.
              </div>
            </div>
          )}

          <div style={{ marginTop: "1.5rem", marginLeft: "0.5rem" }}>
            {errors.length > 0 &&
              errors.map((err, key) => (
                <span key={key} style={{ display: "block", color: "#f5222d" }}>
                  {err}
                </span>
              ))}

            {strangeHeaders.length > 0 && (
              <span style={{ display: "block", color: "#f5222d" }}>
                {strings.missingOrInvalidColumnHeader}
              </span>
            )}
          </div>

        </div>
      </Modal>
    );
  }
}

export default connect(null, { importCustomers })(ImportCustomerModal);
